// src/components/study/QuickAddModal.tsx
// =====================================================================
// MODAL ADICIONAR AOS MEUS DECKS - IDENTIDADE GRAIA
// Estrutura otimizada para listas longas, sem scroll duplo
// =====================================================================

import React, { useState, useEffect } from 'react';
import { X, Plus, Folder, FolderOpen, Layers, ChevronRight } from 'lucide-react';
import './QuickAddModal.css';

interface QuickAddModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentCard: {
        _id: string;
        deckId: string;
        subdeckId: string;
        front: string;
        back: string;
    };
    onSuccess: (message: string) => void;
}

interface CustomDeck {
    _id: string;
    name: string;
    subdecks: Array<{
        _id: string;
        name: string;
        cards: any[];
    }>;
}

const QuickAddModal: React.FC<QuickAddModalProps> = ({
    isOpen,
    onClose,
    currentCard,
    onSuccess
}) => {
    const [myDecks, setMyDecks] = useState<CustomDeck[]>([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState<'list' | 'createDeck' | 'createSubdeck'>('list');

    // Estados para criar deck
    const [newDeckName, setNewDeckName] = useState('');
    const [newDeckDescription, setNewDeckDescription] = useState('');

    // Estados para criar subdeck
    const [selectedDeckForNewSubdeck, setSelectedDeckForNewSubdeck] = useState<string>('');
    const [newSubdeckName, setNewSubdeckName] = useState('');
    const [newSubdeckDescription, setNewSubdeckDescription] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadMyDecks();
        }
    }, [isOpen]);

    const loadMyDecks = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/custom-decks/my-decks', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setMyDecks(data);
            }
        } catch (error) {
            console.error('Erro ao carregar decks:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToSubdeck = async (deckId: string, subdeckId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(
                `/api/custom-decks/${deckId}/subdecks/${subdeckId}/cards`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        deckId: currentCard.deckId,
                        subdeckId: currentCard.subdeckId,
                        cardId: currentCard._id,
                        front: currentCard.front,
                        back: currentCard.back
                    })
                }
            );

            if (response.ok) {
                onSuccess('Card adicionado com sucesso!');
                handleClose();
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao adicionar card');
            }
        } catch (error) {
            console.error('Erro ao adicionar card:', error);
            alert('Erro ao adicionar card');
        }
    };

    const handleCreateDeck = async () => {
        if (!newDeckName.trim()) {
            alert('Nome do deck é obrigatório');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/custom-decks', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: newDeckName.trim(),
                    description: newDeckDescription.trim()
                })
            });

            if (response.ok) {
                const data = await response.json();
                setSelectedDeckForNewSubdeck(data._id);
                setView('createSubdeck');
                setNewDeckName('');
                setNewDeckDescription('');
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao criar deck');
            }
        } catch (error) {
            console.error('Erro ao criar deck:', error);
            alert('Erro ao criar deck');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSubdeck = async () => {
        if (!newSubdeckName.trim()) {
            alert('Nome do subdeck é obrigatório');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/custom-decks/${selectedDeckForNewSubdeck}/subdecks`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        name: newSubdeckName.trim(),
                        description: newSubdeckDescription.trim()
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                const newSubdeckId = data.subdecks[data.subdecks.length - 1]._id;

                await handleAddToSubdeck(selectedDeckForNewSubdeck, newSubdeckId);

                await loadMyDecks();
                setView('list');
                setNewSubdeckName('');
                setNewSubdeckDescription('');
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao criar subdeck');
            }
        } catch (error) {
            console.error('Erro ao criar subdeck:', error);
            alert('Erro ao criar subdeck');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setView('list');
        setNewDeckName('');
        setNewDeckDescription('');
        setNewSubdeckName('');
        setNewSubdeckDescription('');
        setSelectedDeckForNewSubdeck('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="quickadd-overlay" onClick={handleClose}>
            <div className="quickadd-container" onClick={(e) => e.stopPropagation()}>
                {/* ========== HEADER FIXO ========== */}
                <div className="quickadd-header">
                    <div className="quickadd-title-wrapper">
                        <Plus size={20} className="quickadd-icon" />
                        <h3 className="quickadd-title">
                            {view === 'list' && 'Adicionar aos Meus Decks'}
                            {view === 'createDeck' && 'Criar Novo Deck'}
                            {view === 'createSubdeck' && 'Criar Subdeck'}
                        </h3>
                    </div>
                    <button
                        className="quickadd-close"
                        onClick={handleClose}
                        aria-label="Fechar"
                        disabled={loading}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* ========== BODY SCROLLÁVEL ========== */}
                <div className="quickadd-body">
                    {/* VIEW: LISTA DE DECKS */}
                    {view === 'list' && (
                        <>
                            {loading ? (
                                <div className="quickadd-loading">
                                    <div className="quickadd-spinner" />
                                    <p>Carregando decks...</p>
                                </div>
                            ) : (
                                <>
                                    <button
                                        className="quickadd-btn-create-deck"
                                        onClick={() => setView('createDeck')}
                                    >
                                        <Plus size={18} />
                                        Criar Novo Deck
                                    </button>

                                    {myDecks.length === 0 ? (
                                        <div className="quickadd-empty">
                                            <Folder size={48} className="quickadd-empty-icon" />
                                            <p>Você ainda não tem decks personalizados</p>
                                            <span>Crie seu primeiro deck para começar</span>
                                        </div>
                                    ) : (
                                        <>
                                            <p className="quickadd-hint">
                                                Selecione um subdeck para adicionar este card
                                            </p>

                                            <div className="quickadd-deck-list">
                                                {myDecks.map(deck => (
                                                    <div key={deck._id} className="quickadd-deck-group">
                                                        <div className="quickadd-deck-header">
                                                            <div className="quickadd-deck-name">
                                                                <FolderOpen size={18} />
                                                                <span>{deck.name}</span>
                                                            </div>
                                                            <button
                                                                className="quickadd-btn-add-subdeck"
                                                                onClick={() => {
                                                                    setSelectedDeckForNewSubdeck(deck._id);
                                                                    setView('createSubdeck');
                                                                }}
                                                                title="Criar subdeck"
                                                            >
                                                                <Plus size={14} />
                                                                Subdeck
                                                            </button>
                                                        </div>

                                                        {deck.subdecks && deck.subdecks.length > 0 ? (
                                                            <div className="quickadd-subdeck-list">
                                                                {deck.subdecks.map(subdeck => (
                                                                    <button
                                                                        key={subdeck._id}
                                                                        className="quickadd-subdeck-btn"
                                                                        onClick={() => handleAddToSubdeck(deck._id, subdeck._id)}
                                                                    >
                                                                        <div className="quickadd-subdeck-info">
                                                                            <Layers size={16} />
                                                                            <span>{subdeck.name}</span>
                                                                        </div>
                                                                        <div className="quickadd-subdeck-meta">
                                                                            <span className="quickadd-card-count">
                                                                                {subdeck.cards?.length || 0} cards
                                                                            </span>
                                                                            <ChevronRight size={16} />
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="quickadd-no-subdecks">
                                                                Nenhum subdeck criado ainda
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* VIEW: CRIAR DECK */}
                    {view === 'createDeck' && (
                        <>
                            <p className="quickadd-hint">
                                Crie um deck para organizar seus flashcards
                            </p>

                            <div className="quickadd-form">
                                <div className="quickadd-form-group">
                                    <label className="quickadd-label">Nome do Deck *</label>
                                    <input
                                        type="text"
                                        className="quickadd-input"
                                        value={newDeckName}
                                        onChange={(e) => setNewDeckName(e.target.value)}
                                        placeholder="Ex: Anatomia Cardiovascular"
                                        autoFocus
                                        disabled={loading}
                                    />
                                </div>

                                <div className="quickadd-form-group">
                                    <label className="quickadd-label">Descrição (opcional)</label>
                                    <textarea
                                        className="quickadd-textarea"
                                        value={newDeckDescription}
                                        onChange={(e) => setNewDeckDescription(e.target.value)}
                                        placeholder="Breve descrição do conteúdo..."
                                        rows={3}
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* VIEW: CRIAR SUBDECK */}
                    {view === 'createSubdeck' && (
                        <>
                            <p className="quickadd-hint">
                                Crie um subdeck para adicionar o card
                            </p>

                            <div className="quickadd-form">
                                <div className="quickadd-form-group">
                                    <label className="quickadd-label">Nome do Subdeck *</label>
                                    <input
                                        type="text"
                                        className="quickadd-input"
                                        value={newSubdeckName}
                                        onChange={(e) => setNewSubdeckName(e.target.value)}
                                        placeholder="Ex: Coração e Vasos"
                                        autoFocus
                                        disabled={loading}
                                    />
                                </div>

                                <div className="quickadd-form-group">
                                    <label className="quickadd-label">Descrição (opcional)</label>
                                    <textarea
                                        className="quickadd-textarea"
                                        value={newSubdeckDescription}
                                        onChange={(e) => setNewSubdeckDescription(e.target.value)}
                                        placeholder="Breve descrição..."
                                        rows={3}
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* ========== FOOTER FIXO ========== */}
                <div className="quickadd-footer">
                    {view === 'list' && (
                        <button
                            className="quickadd-btn-cancel"
                            onClick={handleClose}
                        >
                            Fechar
                        </button>
                    )}

                    {view === 'createDeck' && (
                        <>
                            <button
                                className="quickadd-btn-back"
                                onClick={() => setView('list')}
                                disabled={loading}
                            >
                                Voltar
                            </button>
                            <button
                                className="quickadd-btn-submit"
                                onClick={handleCreateDeck}
                                disabled={loading || !newDeckName.trim()}
                            >
                                {loading ? 'Criando...' : 'Próximo'}
                            </button>
                        </>
                    )}

                    {view === 'createSubdeck' && (
                        <>
                            <button
                                className="quickadd-btn-back"
                                onClick={() => {
                                    setView('list');
                                    setSelectedDeckForNewSubdeck('');
                                }}
                                disabled={loading}
                            >
                                Voltar
                            </button>
                            <button
                                className="quickadd-btn-submit"
                                onClick={handleCreateSubdeck}
                                disabled={loading || !newSubdeckName.trim()}
                            >
                                {loading ? 'Adicionando...' : 'Criar e Adicionar'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuickAddModal;